/**
 * PWA Install Prompt Component
 * Provides a native-like install experience for the adult community platform PWA
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { X, Download, Smartphone, Monitor } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUiStore } from '@/stores/uiStore'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

interface PWAInstallPromptProps {
  className?: string
  showDelay?: number
  autoShow?: boolean
}

export function PWAInstallPrompt({
  className,
  showDelay = 3000,
  autoShow = true
}: PWAInstallPromptProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [platform, setPlatform] = useState<string>('')
  const { isMobileChatOverlayOpen } = useUiStore()

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone === true ||
                         document.referrer.includes('android-app://')

    setIsInstalled(isStandalone)

    if (isStandalone) return

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase()
    if (userAgent.includes('android')) {
      setPlatform('android')
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      setPlatform('ios')
    } else {
      setPlatform('desktop')
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      
      // Show prompt after delay if autoShow is enabled
      if (autoShow && !hasUserDismissed()) {
        setTimeout(() => {
          setShowPrompt(true)
        }, showDelay)
      }
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setShowPrompt(false)
      setDeferredPrompt(null)
      
      // Track installation
      trackInstallEvent('installed')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [autoShow, showDelay])

  const hasUserDismissed = (): boolean => {
    const dismissed = localStorage.getItem('pwa-prompt-dismissed')
    if (!dismissed) return false
    
    const dismissedDate = new Date(dismissed)
    const now = new Date()
    const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
    
    // Show again after 7 days
    return daysSinceDismissed < 7
  }

  const handleInstall = async () => {
    if (!deferredPrompt) return

    try {
      await deferredPrompt.prompt()
      const result = await deferredPrompt.userChoice
      
      trackInstallEvent(result.outcome)
      
      if (result.outcome === 'accepted') {
        setShowPrompt(false)
      } else {
        handleDismiss()
      }
      
      setDeferredPrompt(null)
    } catch (error) {

      trackInstallEvent('error')
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString())
    trackInstallEvent('dismissed')
  }

  const trackInstallEvent = (outcome: string) => {
    try {
      fetch('/api/analytics/pwa-install', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          outcome,
          platform,
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        })
      }).catch(() => {
        // Ignore analytics errors
      })
    } catch (error) {
      // Ignore analytics errors
    }
  }

  const getInstallInstructions = () => {
    switch (platform) {
      case 'ios':
        return {
          title: 'Install Community App',
          description: 'Add to your home screen for the best experience',
          steps: [
            'Tap the share button in Safari',
            'Select "Add to Home Screen"',
            'Tap "Add" to confirm'
          ],
          icon: <Smartphone className="w-6 h-6" />
        }
      
      case 'android':
        return {
          title: 'Install Community App',
          description: 'Get native app-like experience',
          steps: [
            'Tap "Install" below',
            'Confirm installation',
            'App will be added to your home screen'
          ],
          icon: <Smartphone className="w-6 h-6" />
        }
      
      default:
        return {
          title: 'Install Community App',
          description: 'Access the app directly from your desktop',
          steps: [
            'Click "Install" below',
            'Confirm installation',
            'App will be added to your applications'
          ],
          icon: <Monitor className="w-6 h-6" />
        }
    }
  }

  // Don't show if already installed, no prompt available, or mobile chat is open
  if (isInstalled || (!showPrompt && autoShow) || isMobileChatOverlayOpen) return null

  const instructions = getInstallInstructions()

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
          className={cn(
            'fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50',
            className
          )}
        >
          <Card className="bg-gray-900/95 backdrop-blur-sm border-gray-700 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {instructions.icon}
                <div>
                  <h3 className="font-semibold text-white text-sm">
                    {instructions.title}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    {instructions.description}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="p-1 h-auto text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {platform === 'ios' ? (
              <div className="space-y-2 mb-4">
                {instructions.steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs text-gray-300">
                    <span className="flex-shrink-0 w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs">
                      {index + 1}
                    </span>
                    {step}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-2 mt-4">
                {deferredPrompt && (
                  <Button
                    onClick={handleInstall}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Install App
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleDismiss}
                  size="sm"
                  className="text-gray-300 border-gray-600 hover:bg-gray-800"
                >
                  Later
                </Button>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-gray-700">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Works offline • Fast loading • Native experience
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Hook for managing PWA installation state
 */
export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as any).standalone === true ||
                         document.referrer.includes('android-app://')

    setIsInstalled(isStandalone)

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      setCanInstall(true)
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      setCanInstall(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const install = async () => {
    if (!deferredPrompt) return false

    try {
      await deferredPrompt.prompt()
      const result = await deferredPrompt.userChoice
      
      setDeferredPrompt(null)
      setCanInstall(false)
      
      return result.outcome === 'accepted'
    } catch (error) {

      return false
    }
  }

  return {
    canInstall,
    isInstalled,
    install
  }
}